import { AxiosError } from 'axios';
import { ErrorNotificationData } from '../../components/Common/ErrorNotification';

/**
 * Typ funkcji callback do wyświetlania błędów
 */
export type ErrorDisplayCallback = (error: Omit<ErrorNotificationData, 'id' | 'timestamp'>) => void;

/**
 * Globalny logger błędów - przechwytuje wszystkie błędy HTTP i wyświetla je na ekranie
 * Zamiast ponawiania requestów, loguje błędy i pokazuje czerwoną plakietkę
 */
export class GlobalErrorLogger {
  private static instance: GlobalErrorLogger;
  private errorDisplayCallback: ErrorDisplayCallback | null = null;

  private constructor() {}

  static getInstance(): GlobalErrorLogger {
    if (!GlobalErrorLogger.instance) {
      GlobalErrorLogger.instance = new GlobalErrorLogger();
    }
    return GlobalErrorLogger.instance;
  }

  /**
   * Rejestruje callback do wyświetlania błędów
   */
  setErrorDisplayCallback(callback: ErrorDisplayCallback): void {
    this.errorDisplayCallback = callback;
  }

  /**
   * Loguje błąd HTTP i wyświetla go na ekranie
   */
  logHttpError(error: AxiosError, context?: string): void {
    const errorMessage = this.formatErrorMessage(error);
    const errorDetails = this.extractErrorDetails(error);

    // Loguj do konsoli dla debugowania
    console.error(`🔴 Błąd aplikacji${context ? ` (${context})` : ''}:`, {
      message: errorMessage,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      details: errorDetails,
      timestamp: new Date().toISOString()
    });

    // Wyświetl błąd na ekranie jeśli callback jest zarejestrowany
    if (this.errorDisplayCallback) {
      this.errorDisplayCallback({
        message: errorMessage,
        details: errorDetails,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status
      });
    }
  }

  /**
   * Loguje błąd ogólny (nie HTTP)
   */
  logGeneralError(error: Error, context?: string): void {
    const errorMessage = error.message || 'Nieznany błąd aplikacji';

    // Loguj do konsoli
    console.error(`🔴 Błąd aplikacji${context ? ` (${context})` : ''}:`, {
      message: errorMessage,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Wyświetl błąd na ekranie
    if (this.errorDisplayCallback) {
      this.errorDisplayCallback({
        message: errorMessage,
        details: error.stack || 'Brak dodatkowych szczegółów'
      });
    }
  }

  /**
   * Formatuje wiadomość błędu dla użytkownika
   */
  private formatErrorMessage(error: AxiosError): string {
    const status = error.response?.status;
    
    switch (status) {
      case 400:
        return 'Nieprawidłowe żądanie - sprawdź wprowadzone dane';
      case 401:
        return 'Błąd autoryzacji - zaloguj się ponownie';
      case 403:
        return 'Brak uprawnień do wykonania tej operacji';
      case 404:
        return 'Żądany zasób nie został znaleziony';
      case 500:
        return 'Błąd serwera - spróbuj ponownie lub skontaktuj się z pomocą';
      case 502:
        return 'Błąd bramy serwera - serwer jest tymczasowo niedostępny';
      case 503:
        return 'Serwis niedostępny - spróbuj ponownie za chwilę';
      case 504:
        return 'Przekroczono limit czasu odpowiedzi serwera';
      default:
        if (status) {
          return `Błąd HTTP ${status} - ${error.message}`;
        }
        return error.message || 'Błąd połączenia z serwerem';
    }
  }

  /**
   * Wyciąga szczegóły błędu dla deweloperów
   */
  private extractErrorDetails(error: AxiosError): string {
    const details: string[] = [];
    
    if (error.response?.data) {
      try {
        const responseData = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data, null, 2);
        details.push(`Response: ${responseData}`);
      } catch {
        details.push(`Response: ${error.response.data}`);
      }
    }

    if (error.code) {
      details.push(`Code: ${error.code}`);
    }

    if (error.stack) {
      details.push(`Stack: ${error.stack}`);
    }

    return details.join('\n') || 'Brak dodatkowych szczegółów';
  }

  /**
   * Sprawdza czy błąd powinien być wyświetlony (filtruje niektóre błędy)
   */
  shouldDisplayError(error: AxiosError): boolean {
    // Nie wyświetlaj błędów 401 jeśli są obsługiwane przez auth interceptor
    if (error.response?.status === 401) {
      return false;
    }

    // Nie wyświetlaj błędów anulowanych requestów
    if (error.code === 'ERR_CANCELED') {
      return false;
    }

    return true;
  }
}

/**
 * Funkcja pomocnicza do logowania błędów HTTP
 */
export const logHttpError = (error: AxiosError, context?: string): void => {
  const logger = GlobalErrorLogger.getInstance();
  if (logger.shouldDisplayError(error)) {
    logger.logHttpError(error, context);
  }
};

/**
 * Funkcja pomocnicza do logowania błędów ogólnych
 */
export const logGeneralError = (error: Error, context?: string): void => {
  const logger = GlobalErrorLogger.getInstance();
  logger.logGeneralError(error, context);
};

/**
 * Funkcja do rejestrowania callback do wyświetlania błędów
 */
export const setErrorDisplayCallback = (callback: ErrorDisplayCallback): void => {
  const logger = GlobalErrorLogger.getInstance();
  logger.setErrorDisplayCallback(callback);
};

export default GlobalErrorLogger;